import { useState } from 'react';
import { useUpdateObject } from '@/features/objects/hooks';
import { apiGet } from '@/lib/api/client';
import type { TaskCardData } from './types';

/**
 * ONE card holding ALL matched tasks, rendered as a checkbox list. Each row is
 * a checkbox + title (+ optional due date).
 *
 * The checkbox writeback is LOCAL (NOT via onCardAction): on toggle we fetch a
 * fresh ETag for the task (the card data carries none), then PATCH the status
 * via useUpdateObject('task'). That mutation invalidates queryKeys.objects
 * .byType('task') so the Tasks module stays in sync. Local optimistic state
 * keeps the checkbox responsive while the request is in flight.
 */
export function TaskResultCard({ data }: { data: TaskCardData }) {
  const tasks = data.tasks ?? [];
  const update = useUpdateObject('task');

  // Local optimistic checked state, keyed by task id, seeded from the card data.
  const [checked, setChecked] = useState<Record<string, boolean>>(() => {
    const seed: Record<string, boolean> = {};
    for (const t of tasks) seed[t.id] = !!t.completed;
    return seed;
  });

  if (!tasks.length) return null;

  async function toggle(id: string, nextCompleted: boolean) {
    // Optimistic flip.
    setChecked((prev) => ({ ...prev, [id]: nextCompleted }));
    try {
      // The card data has no ETag — fetch the current task to read it.
      const { data: task, etag } = await apiGet<{ _etag?: string; version?: number }>(`/tasks/${id}`);
      const resolvedEtag = etag ?? task?._etag ?? String(task?.version ?? '');
      await update.mutateAsync({
        id,
        etag: resolvedEtag,
        // completed checkbox → 'done'; unchecked → 'todo'.
        patch: { status: nextCompleted ? 'done' : 'todo' } as any,
      });
    } catch {
      // Roll back the optimistic flip on failure.
      setChecked((prev) => ({ ...prev, [id]: !nextCompleted }));
    }
  }

  return (
    <div className="rc-card rc-tasks">
      <div className="rc-meta">
        <span className="rc-meta-kind">✅ TASKS</span>
        <span className="rc-meta-sep">·</span>
        <span className="rc-meta-match">{tasks.length}</span>
      </div>

      <ul className="rc-task-list">
        {tasks.map((t) => {
          const isChecked = checked[t.id] ?? !!t.completed;
          return (
            <li key={t.id} className="rc-task-row">
              <label className="rc-task-label">
                <input
                  type="checkbox"
                  className="rc-task-check"
                  checked={isChecked}
                  onChange={(e) => {
                    e.stopPropagation();
                    void toggle(t.id, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                <span className={`rc-task-title${isChecked ? ' rc-task-done' : ''}`}>{t.title}</span>
                {t.due && <span className="rc-task-due">{t.due}</span>}
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
